import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge, Button, Card, Field, Input, NativeSelect, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@vit/ui';
import { useApiMutation } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { applyLocale, useTranslation } from '../../lib/i18n.js';
import { useMe, useSession } from '../../lib/session.js';
import { PageHeader, SectionTitle } from '../../components/common.js';
import { NotificationPreferences } from './notification-preferences.js';
import { LinkedAccounts } from './linked-accounts.js';

const TIMEZONES = ['America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City', 'America/Bogota', 'America/Santiago', 'America/New_York', 'Europe/Lisbon', 'Europe/Madrid', 'UTC'];

export function SettingsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const { refreshMe } = useSession();
  const [params, setParams] = useSearchParams();
  const [displayName, setDisplayName] = useState(me.displayName);
  const [locale, setLocale] = useState(me.locale);
  const [timezone, setTimezone] = useState(me.timezone);
  const update = useApiMutation('updateMe');
  const timezones = TIMEZONES.includes(me.timezone) ? TIMEZONES : [me.timezone, ...TIMEZONES];

  const save = async () => {
    try {
      await update.mutateAsync({ body: { displayName, locale, timezone } });
      applyLocale(locale);
      await refreshMe();
      toast.success(t('common.changesSaved'));
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('settings.title')} />
      <div className="mx-auto w-full max-w-3xl p-4 md:p-6">
        <Tabs value={params.get('tab') ?? 'profile'} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList>
            <TabsTrigger value="profile">{t('settings.profile')}</TabsTrigger>
            <TabsTrigger value="notifications">{t('settings.notifications')}</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="flex flex-col gap-6 pt-4">
            <Card className="flex flex-col gap-4 p-5">
              <Field label={t('settings.displayName')} htmlFor="s-name">
                <Input id="s-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t('settings.language')} htmlFor="s-locale">
                  <NativeSelect id="s-locale" value={locale} onChange={(e) => setLocale(e.target.value as 'en')}>
                    <option value="en">English</option>
                    <option value="pt-BR">Português (Brasil)</option>
                    <option value="es">Español</option>
                  </NativeSelect>
                </Field>
                <Field label={t('settings.timezone')} htmlFor="s-tz">
                  <NativeSelect id="s-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
              <Button variant="primary" className="self-end" onClick={() => void save()} loading={update.isPending}>
                {t('common.save')}
              </Button>
            </Card>
            <Card className="p-5">
              <SectionTitle>{t('settings.roles')}</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {me.roles.length ? me.roles.map((r) => <Badge key={r} tone="accent">{r}</Badge>) : <Badge>{t('settings.basic')}</Badge>}
                {me.capabilities.map((c) => (
                  <Badge key={c} tone="outline">
                    {c}
                  </Badge>
                ))}
                {me.teams.map((team) => (
                  <Badge key={team.id} tone="neutral">
                    {team.name} · {team.role === 'MANAGER' ? t('teams.manager') : t('teams.member')}
                  </Badge>
                ))}
              </div>
            </Card>
            <LinkedAccounts />
          </TabsContent>
          <TabsContent value="notifications" className="pt-4">
            <NotificationPreferences />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
